import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import AppShell from "../components/layout/AppShell";
import { InboxAuditPromoEmptyState } from "../components/audit/InboxAuditPromoEmptyState.jsx";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { PageFirstLoadSpinner } from "../components/ui/PageFirstLoadSpinner.jsx";
import BarTrendChart, { BAR_SEGMENT_META, useBarTrendPalette } from "../components/charts/BarTrendChart";
import MatchRateDonut from "../components/charts/MatchRateDonut";
import { useMailboxScope } from "../contexts/MailboxScopeContext";
import { useTheme } from "../contexts/ThemeContext";
import { analyticsService } from "../services/analytics";
import { brandProfileService } from "../services/brandProfile";
import { getScoreTextClass } from "../utils/scoreColors";
import { hexToRgba } from "../utils/themes";

const RANGE_OPTIONS = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "365d", label: "1Y" },
  { value: "all", label: "All time" },
];

const ANALYTICS_PAGE_SUBTITLE = "Volume, ICP matches, and reply outcomes for this mailbox";

function AnalyticsConversionFunnel({ funnelRows }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-x-6 gap-y-6 md:gap-x-10 md:gap-y-5 w-full py-2 border-b border-theme-border">
      {funnelRows.map((item) => (
        <div key={item.label} className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">{item.label}</div>
          <div className="mt-1 text-2xl md:text-3xl lg:text-4xl font-bold text-theme-on-background tabular-nums leading-tight">
            {item.value}
          </div>
          {item.convPct !== null ? (
            <div className="mt-1 font-mono text-[11px] text-theme-text opacity-55 tabular-nums">
              {item.convPct}% conversion
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function MailboxBrandLeadMatrixCard({ title, brands, rows, theme, heatmapMax, mode }) {
  const accent = theme.accent;
  return (
    <Card>
      <h3 className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim mb-4">{title}</h3>
      <div className="overflow-x-auto rounded-lg border border-theme-border">
        <table className="w-full border-collapse text-left font-mono text-[11px]">
          <thead>
            <tr className="border-b border-theme-border bg-theme-secondary/40">
              <th className="sticky left-0 z-[1] min-w-[120px] border-r border-theme-border bg-theme-secondary/80 px-3 py-2 text-[10px] uppercase tracking-[0.08em] text-theme-dim">
                Mailbox
              </th>
              {brands.map((col) => (
                <th
                  key={col.hash}
                  className="border-r border-theme-border px-3 py-2 text-center text-[10px] uppercase tracking-[0.08em] text-theme-dim last:border-r-0"
                >
                  {col.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.mailbox_hash} className="border-b border-theme-border last:border-b-0">
                <td className="sticky left-0 z-[1] min-w-[120px] border-r border-theme-border bg-theme-main px-3 py-2 text-theme-text">
                  {row.mailbox_label}
                </td>
                {(mode === "count" ? row.counts : row.scores).map((cell, j) => {
                  const num = typeof cell === "number" ? cell : null;
                  const intensity =
                    num != null && heatmapMax > 0 ? (num / heatmapMax) * 0.5 : 0;
                  const display =
                    mode === "count"
                      ? String(cell ?? 0)
                      : cell == null
                        ? "—"
                        : `${cell}%`;
                  return (
                    <td
                      key={brands[j].hash}
                      className="border-r border-theme-border px-3 py-2 text-center font-mono text-theme-text last:border-r-0"
                      style={{
                        backgroundColor:
                          num != null && num > 0 && heatmapMax > 0
                            ? hexToRgba(accent, intensity)
                            : undefined,
                      }}
                    >
                      {display}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function formatIcpPeriodDelta(curr, prior, comparisonLabel) {
  if (comparisonLabel == null || prior == null) return null;
  if (prior === 0 && curr === 0) return `Same as ${comparisonLabel}`;
  if (prior === 0) return `Up from zero in ${comparisonLabel}`;
  const raw = ((curr - prior) / prior) * 100;
  const abs = Math.round(Math.abs(raw) * 10) / 10;
  if (raw > 0) return `${abs}% more than ${comparisonLabel}`;
  if (raw < 0) return `${abs}% fewer than ${comparisonLabel}`;
  return `Same as ${comparisonLabel}`;
}

const AnalyticsPage = (props) => {
  const [range, setRange] = useState("30d");
  /** Empty string = all brands (API omits brands filter). */
  const [selectedBrandHash, setSelectedBrandHash] = useState("");
  const mailboxScope = useMailboxScope();
  const { theme } = useTheme();
  const barPalette = useBarTrendPalette();
  const [selectedBarSegments, setSelectedBarSegments] = useState(() => new Set());

  const brandsQuery = useQuery({
    queryKey: ["brand-profiles-all"],
    queryFn: brandProfileService.listAll,
  });
  const brands = brandsQuery.data?.results ?? [];
  const showBrandSelect = brands.length > 1;

  useEffect(() => {
    if (showBrandSelect) return;
    setSelectedBrandHash("");
  }, [showBrandSelect]);

  const brandFilterList = selectedBrandHash ? [selectedBrandHash] : [];

  const lifetimeQuery = useQuery({
    queryKey: ["analytics", "all", mailboxScope?.mailboxHash, "inbox-empty-gate"],
    queryFn: () =>
      analyticsService.fetch("all", {
        mailbox: mailboxScope?.mailboxHash || "",
        brands: [],
      }),
    enabled: Boolean(mailboxScope?.mailboxHash) && !mailboxScope?.mailboxesLoading,
  });

  const { data, isLoading: chartsLoading } = useQuery({
    queryKey: ["analytics", range, mailboxScope?.mailboxHash, selectedBrandHash || "all"],
    queryFn: () =>
      analyticsService.fetch(range, {
        mailbox: mailboxScope?.mailboxHash || "",
        brands: brandFilterList,
      }),
    enabled: Boolean(mailboxScope?.mailboxHash) && !mailboxScope?.mailboxesLoading,
    placeholderData: (previousData) => previousData,
  });

  const importedAllTime = lifetimeQuery.data?.kpis?.emails_imported ?? 0;
  const analyticsGateReady = Boolean(mailboxScope?.mailboxHash) && !lifetimeQuery.isLoading;
  const noFetchedEmails = analyticsGateReady && importedAllTime === 0;
  const showAnalyticsCharts = analyticsGateReady && importedAllTime > 0;

  const scopeBooting = Boolean(mailboxScope?.mailboxesLoading);
  const mailboxReady = Boolean(mailboxScope?.mailboxHash) && !scopeBooting;
  const showAnalyticsBodySpinner =
    scopeBooting ||
    (mailboxReady && lifetimeQuery.isLoading) ||
    (mailboxReady && showAnalyticsCharts && chartsLoading);

  const kpis = data?.kpis || {};

  let timeframe = "last 7 days";
  if (range === "all") {
    timeframe = "all time";
  } else if (range === "30d") {
    timeframe = "last 30 days";
  } else if (range === "365d") {
    timeframe = "last year";
  } else if (range !== "7d") {
    timeframe = `last ${range.replace("d", " days")}`;
  }

  const filteredBarChart = useMemo(() => {
    const rows = data?.bar_chart ?? [];
    let activeKeys = selectedBarSegments;
    if (
      activeKeys.size === 0 ||
      activeKeys.size === BAR_SEGMENT_META.length
    ) {
      return rows;
    }
    return rows.map((row) => ({
      ...row,
      other: activeKeys.has("other") ? row.other : 0,
      pitches: activeKeys.has("pitches") ? row.pitches : 0,
      qualified: activeKeys.has("qualified") ? row.qualified : 0,
    }));
  }, [data?.bar_chart, selectedBarSegments]);

  const funnelRows = useMemo(() => {
    const items = [
      { label: "Emails", value: kpis.emails_imported || 0 },
      { label: "Incoming pitch", value: kpis.cold_pitches_detected || 0 },
      { label: "ICP Match", value: kpis.qualified_leads || 0 },
      { label: "Replies sent", value: kpis.flips_sent || 0 },
      { label: "Positive responses", value: kpis.positive_responses || 0 },
    ];
    return items.map((item, idx) => {
      const prevVal = idx > 0 ? items[idx - 1].value : null;
      let convPct = null;
      if (idx > 0) {
        if (prevVal > 0) {
          convPct = Math.round((item.value / prevVal) * 1000) / 10;
        } else {
          convPct = 0;
        }
      }
      return { ...item, convPct };
    });
  }, [kpis]);

  const leadCountHeatmap = data?.lead_count_heatmap || { brands: [], rows: [] };
  const leadAvgFitHeatmap = data?.lead_avg_fit_heatmap || { brands: [], rows: [] };

  const leadCountHeatMax = useMemo(() => {
    let max = 0;
    leadCountHeatmap.rows?.forEach((r) => {
      r.counts?.forEach((c) => {
        if (typeof c === "number" && c > max) max = c;
      });
    });
    return max;
  }, [leadCountHeatmap]);

  const leadFitHeatMax = useMemo(() => {
    let max = 0;
    leadAvgFitHeatmap.rows?.forEach((r) => {
      r.scores?.forEach((s) => {
        if (typeof s === "number" && s > max) max = s;
      });
    });
    return max > 0 ? max : 1;
  }, [leadAvgFitHeatmap]);

  const comparisonLine = formatIcpPeriodDelta(
    kpis.qualified_leads ?? 0,
    kpis.qualified_leads_prior_period,
    kpis.icp_comparison_label,
  );

  const taggingActive =
    selectedBarSegments.size > 0 && selectedBarSegments.size < BAR_SEGMENT_META.length;

  const toggleBarSegment = (key) => {
    setSelectedBarSegments((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (next.size === BAR_SEGMENT_META.length) return new Set();
      return next;
    });
  };

  const tagUnselectedTextColor = (segKey) => {
    if (segKey === "other") return barPalette.other.startsWith("#") ? barPalette.other : theme.text;
    return barPalette.accent;
  };

  const tagSelectedBg = (segKey) => {
    if (segKey === "pitches" || segKey === "qualified") return barPalette.accent;
    return barPalette.other.startsWith("#") ? barPalette.other : barPalette.accent;
  };

  const hasLeadMatrixData =
    (leadCountHeatmap.brands?.length && leadCountHeatmap.rows?.length) ||
    (leadAvgFitHeatmap.brands?.length && leadAvgFitHeatmap.rows?.length);
  const showLeadMatrices =
    ((mailboxScope?.mailboxes ?? []).length > 1 || brands.length > 1) && hasLeadMatrixData;

  const showAnalyticsFilters = showAnalyticsCharts;

  return (
    <AppShell
      currentUser={props.currentUser}
      pageTitle="Performance Analytics"
      pageSubtitle={ANALYTICS_PAGE_SUBTITLE}
    >
      <div className="space-y-6">
        <div className="hidden md:flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display font-bold italic text-[40px] leading-[1.1] text-theme-on-background">
              Performance Analytics
            </h1>
            <p className="font-mono text-[12px] text-theme-dim mt-1 uppercase tracking-[0.08em]">
              {ANALYTICS_PAGE_SUBTITLE}
            </p>
          </div>
          {showAnalyticsFilters ? (
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {showBrandSelect ? (
              <select
                aria-label="Filter analytics by brand"
                className="max-w-[200px] rounded-lg border border-theme-border bg-theme-main px-3 py-2 font-mono text-[11px] text-theme-text"
                value={selectedBrandHash}
                onChange={(e) => setSelectedBrandHash(e.target.value)}
              >
                <option value="">All brands</option>
                {brands.map((b) => (
                  <option key={b.hash} value={b.hash}>
                    {b.company_name?.trim() || "Brand"}
                  </option>
                ))}
              </select>
            ) : null}
            <div className="flex items-center gap-2 flex-wrap">
              {RANGE_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  variant={range === option.value ? "primary" : "ghost"}
                  size="sm"
                  onClick={() => setRange(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
          ) : null}
        </div>
        <div className="flex md:hidden flex-col gap-3">
          <h1 className="font-display font-bold italic text-[28px] leading-[1.1] text-theme-on-background">
            Performance Analytics
          </h1>
          <p className="font-mono text-[10px] text-theme-dim uppercase tracking-[0.08em]">
            {ANALYTICS_PAGE_SUBTITLE}
          </p>
          {showAnalyticsFilters ? (
          <>
          {showBrandSelect ? (
            <select
              aria-label="Filter analytics by brand"
              className="w-full rounded-lg border border-theme-border bg-theme-main px-3 py-2 font-mono text-[11px] text-theme-text"
              value={selectedBrandHash}
              onChange={(e) => setSelectedBrandHash(e.target.value)}
            >
              <option value="">All brands</option>
              {brands.map((b) => (
                <option key={b.hash} value={b.hash}>
                  {b.company_name?.trim() || "Brand"}
                </option>
              ))}
            </select>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {RANGE_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={range === option.value ? "primary" : "ghost"}
                size="sm"
                onClick={() => setRange(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
          </>
          ) : null}
        </div>
        {!mailboxScope?.mailboxHash && !scopeBooting ? (
          <Card className="p-8 text-center">
            <p className="text-theme-text">Connect a mailbox in Settings to view analytics.</p>
          </Card>
        ) : null}

        {showAnalyticsBodySpinner ? <PageFirstLoadSpinner /> : null}

        {!showAnalyticsBodySpinner && mailboxScope?.mailboxHash && noFetchedEmails ? <InboxAuditPromoEmptyState /> : null}

        {!showAnalyticsBodySpinner && mailboxScope?.mailboxHash && showAnalyticsCharts ? (
          <>
            <AnalyticsConversionFunnel funnelRows={funnelRows} />

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr_1fr]">
              <Card className="space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim shrink-0">
                    Emails scanned ({timeframe})
                  </span>
                  <div className="flex flex-wrap items-center justify-end gap-2 flex-1 min-w-0">
                    {BAR_SEGMENT_META.map((seg) => {
                      const stroke = barPalette[seg.key];
                      const filled = taggingActive && selectedBarSegments.has(seg.key);
                      return (
                        <button
                          key={seg.key}
                          type="button"
                          onClick={() => toggleBarSegment(seg.key)}
                          className="rounded-md px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em]"
                          style={{
                            borderWidth: "1px",
                            borderStyle: "solid",
                            borderColor: stroke,
                            backgroundColor: filled ? tagSelectedBg(seg.key) : "transparent",
                            color: filled ? "#ffffff" : tagUnselectedTextColor(seg.key),
                          }}
                        >
                          {seg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <BarTrendChart data={filteredBarChart} />
              </Card>

              <Card className="space-y-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">
                  Total ICP matches
                </div>
                <div className="text-5xl font-bold text-theme-on-background">
                  {kpis.qualified_leads || 0}
                </div>
                {comparisonLine ? (
                  <p className="mt-1 font-mono text-[11px] text-theme-text opacity-60">{comparisonLine}</p>
                ) : null}
                {(kpis.icp_matches_by_brand ?? []).length ? (
                  <ul className="mt-2 space-y-0.5 text-left font-mono text-[11px] text-theme-text opacity-70">
                    {(kpis.icp_matches_by_brand ?? []).map((row) => (
                      <li key={row.brand_hash}>
                        {row.brand_name}: {row.count}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </Card>

              <Card className="space-y-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.1em] text-theme-dim">
                  Avg match score
                </div>
                <MatchRateDonut
                  value={kpis.average_match_score || 0}
                  valueClassName={getScoreTextClass(kpis.average_match_score || 0)}
                />
              </Card>
            </div>

            {showLeadMatrices ? (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <MailboxBrandLeadMatrixCard
                  title="Lead count by mailbox × brand"
                  brands={leadCountHeatmap.brands}
                  rows={leadCountHeatmap.rows}
                  theme={theme}
                  heatmapMax={leadCountHeatMax}
                  mode="count"
                />
                <MailboxBrandLeadMatrixCard
                  title="Avg fit of leads by mailbox × brand"
                  brands={leadAvgFitHeatmap.brands}
                  rows={leadAvgFitHeatmap.rows}
                  theme={theme}
                  heatmapMax={leadFitHeatMax}
                  mode="fit"
                />
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </AppShell>
  );
};

export default AnalyticsPage;
